import React, { FunctionComponent, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Field, Form, Formik } from 'formik';
import { McButton, McOption, McSelect } from '@maersk-global/mds-react-wrapper';
import { FormattedMessage } from 'react-intl';
import z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import Drawer from '../../common/components/Drawer/Drawer';
import FormikInputField from '../../common/components/FormikInputField/FormikInputField';
import { WeatherParams } from '../../common/service/weather/types';
import { weatherService } from '../../common/service/weather/weather-service';
import { availableParams, getUnitsByName } from './consts';

type ParametersDrawerProps = {
  open: boolean;
  onSuccess?: Function;
  onClose: () => void;
  selectedTerminal: string;
  defaultValues?: WeatherParams;
  action: 'add' | 'edit';
};

const schema = z.object({
  name: z.string(),
  amount: z.coerce.number({ message: 'Please provide a valid number, i.e. "23.5"' }).min(0.1).max(9999),
});

const ParametersDrawer: FunctionComponent<ParametersDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  selectedTerminal,
  defaultValues,
  action,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const service = useMemo(() => weatherService(), []);

  const getFormInitialValues = () => {
    return {
      id: defaultValues?.id,
      terminalCode: defaultValues?.terminalCode || selectedTerminal,
      name: defaultValues?.name || '',
      amount: defaultValues?.amount || '',
    };
  };

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      if (action === 'edit') {
        await service.updateWeatherParam(values);
        enqueueSnackbar('Parameter updated successfully', { variant: 'success' });
      } else {
        await service.createWeatherParam(values);
        enqueueSnackbar('Parameter added successfully', { variant: 'success' });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={action === 'add' ? 'Add new parameter' : 'Edit parameter'}
      open={open}
      setOpen={onClose}
      onRequestClose={onClose}
      noFooter
    >
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        validateOnChange={false}
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form className="HolidaysDrawer__form">
            <div className="HolidaysDrawer__content">
              <div style={{ width: 250 }}>
                <McSelect
                  label="Name"
                  placeholder="Select parameter"
                  value={values.name}
                  errormessage={errors.name}
                  invalid={Boolean(errors.name)}
                  optionselected={(e) => {
                    setFieldValue('name', (e.target as HTMLInputElement).value);
                  }}
                  name="name"
                  disabled={action === 'edit'}
                >
                  {availableParams.map((option) => (
                    <McOption key={option.id} value={option.label}>
                      {option.label}
                    </McOption>
                  ))}
                </McSelect>
                <br />
                <Field
                  label="Limit"
                  name="amount"
                  suffix={getUnitsByName(values.name)}
                  component={FormikInputField}
                  errormessage={errors.amount}
                  invalid={Boolean(errors.amount)}
                  disabled={!values.name}
                />
              </div>
            </div>
            <div className="HolidaysDrawer__buttons">
              <McButton type="button" appearance="neutral" click={onClose}>
                <FormattedMessage id="cancel" />
              </McButton>
              <McButton disabled={isSubmitting} type="submit" appearance="primary">
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </McButton>
            </div>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default ParametersDrawer;
